// PRODUCTION — admin flow editor
import React, { Fragment, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import {
  modalBackdropCss,
  modalBodyCss,
  modalCardCss,
  modalCategoryLabelCss,
  modalCloseBtnCss,
  modalFooterActionsCss,
  modalFooterCss,
  modalFooterHintCss,
  modalHeaderCss,
  modalTitleCss,
  stepChipCss,
  stepMultiChipBtnCss,
  stepMultiChipCss,
  unsavedModalCardCss,
} from './flowEditorModalStyles'
import { CollectDataStepType } from 'src/constants/collect-data/enums'
import { isFlowEditorAddStepTypeDisabled } from 'src/constants/flowEditorFeatureFlags'
import { STEP_CATEGORIES } from 'src/pages/admin/flow-editor/constants'
import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'

// ─── Shared backdrop + centered card portal ─────────────────────────────────

interface ModalPortalProps {
  children: React.ReactNode
  onBackdropClick?: () => void
}

const ModalPortal = ({ children, onBackdropClick }: ModalPortalProps) =>
  createPortal(
    <Fragment>
      <div onClick={onBackdropClick} css={modalBackdropCss} />
      {children}
    </Fragment>,
    document.body,
  )

// ─── AddStepModal ───────────────────────────────────────────────────────────

// These step types can appear multiple times in a flow
const MULTI_INSTANCE_TYPES = new Set<CollectDataStepType>([
  CollectDataStepType.Form,
  CollectDataStepType.LMSTraining,
  CollectDataStepType.InsuranceCoverage,
  CollectDataStepType.SignContract,
])

interface AddStepModalProps {
  open: boolean
  currentStepTypes: CollectDataStepType[]
  onClose: () => void
  onAdd: (types: CollectDataStepType[]) => void
}

export const AddStepModal = ({ open, currentStepTypes, onClose, onAdd }: AddStepModalProps) => {
  // counts[type] = how many to add (0 = not selected)
  const [counts, setCounts] = useState<Partial<Record<CollectDataStepType, number>>>({})

  const totalCount = Object.values(counts).reduce((s, n) => s + (n ?? 0), 0)

  const setCount = useCallback((type: CollectDataStepType, n: number) => {
    setCounts((prev) => ({ ...prev, [type]: Math.max(0, n) }))
  }, [])

  const handleConfirm = useCallback(() => {
    if (totalCount === 0) {
      return
    }

    const types: CollectDataStepType[] = []

    Object.entries(counts).forEach(([type, n]) => {
      for (let i = 0; i < (n ?? 0); i++) {
        types.push(type as CollectDataStepType)
      }
    })

    onAdd(types)
    setCounts({})
    onClose()
  }, [counts, totalCount, onAdd, onClose])

  const handleClose = useCallback(() => {
    setCounts({})
    onClose()
  }, [onClose])

  if (!open) {
    return null
  }

  let addLabel = 'Add steps'

  if (totalCount > 1) {
    addLabel = `Add ${totalCount} steps`
  } else if (totalCount === 1) {
    addLabel = 'Add 1 step'
  }

  return (
    <ModalPortal onBackdropClick={handleClose}>
      <div css={modalCardCss}>
        <div css={modalHeaderCss}>
          <span css={modalTitleCss}>Add Steps</span>
          <button type="button" onClick={handleClose} css={modalCloseBtnCss}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div css={modalBodyCss}>
          {STEP_CATEGORIES.map((cat) => (
            <div key={cat.label}>
              <div css={modalCategoryLabelCss}>{cat.label}</div>
              <div css={tw`flex flex-wrap gap-1.5`}>
                {cat.types.map((type) => {
                  const isMulti = MULTI_INSTANCE_TYPES.has(type)
                  const inFlow = !isMulti && currentStepTypes.includes(type)
                  const addDisabled = isFlowEditorAddStepTypeDisabled(type)
                  const blocked = inFlow || addDisabled
                  const count = counts[type] ?? 0
                  const isSelected = count > 0

                  if (!isMulti) {
                    return (
                      <button
                        key={type}
                        type="button"
                        disabled={blocked}
                        onClick={() => {
                          if (!blocked) {
                            setCount(type, isSelected ? 0 : 1)
                          }
                        }}
                        css={stepChipCss(blocked, isSelected)}
                      >
                        {(inFlow || isSelected) && (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                        {stepLabel(type)}
                      </button>
                    )
                  }

                  // ── Multi-instance chip with stepper ──────────────────────
                  if (!isSelected) {
                    if (addDisabled) {
                      return (
                        <button key={type} type="button" disabled css={stepChipCss(true, false)}>
                          {stepLabel(type)}
                        </button>
                      )
                    }

                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setCount(type, 1)}
                        css={stepChipCss(false, false)}
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={3}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        {stepLabel(type)}
                      </button>
                    )
                  }

                  return (
                    <div key={type} css={stepMultiChipCss}>
                      <button
                        type="button"
                        onClick={() => setCount(type, count - 1)}
                        css={[stepMultiChipBtnCss, tw`border-r border-primary-300 border-solid`]}
                      >
                        {count === 1 ? (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14H6L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4h6v2" />
                          </svg>
                        ) : (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        )}
                      </button>
                      <span
                        css={tw`min-w-[16px] select-none pl-2 pr-1 text-center text-[13px] font-semibold text-primary-700`}
                      >
                        {count}×
                      </span>
                      <span css={tw`select-none pr-2 text-[13px] font-semibold text-primary-700`}>
                        {stepLabel(type)}
                      </span>
                      <button
                        type="button"
                        disabled={addDisabled}
                        onClick={() => {
                          if (!addDisabled) {
                            setCount(type, count + 1)
                          }
                        }}
                        css={[
                          stepMultiChipBtnCss,
                          tw`border-l border-primary-300 border-solid`,
                          addDisabled ? tw`cursor-not-allowed opacity-40` : undefined,
                        ]}
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={3}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <div css={modalFooterCss}>
          <span css={modalFooterHintCss}>
            {totalCount > 0
              ? `${totalCount} step${totalCount > 1 ? 's' : ''} to add`
              : 'Select steps to add'}
          </span>
          <div css={modalFooterActionsCss}>
            <Button
              variant="outlined"
              size="small"
              color="white"
              onClick={handleClose}
              className={tw`!border-shades-200 !text-shades-700 hover:!bg-shades-50`}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color={totalCount > 0 ? 'primary' : 'grey'}
              onClick={handleConfirm}
              disabled={totalCount === 0}
            >
              {addLabel}
            </Button>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}

// ─── UnsavedChangesModal ────────────────────────────────────────────────────

interface UnsavedChangesModalProps {
  open: boolean
  onLeave: () => void
  onStay: () => void
}

// ─── RevertChangesModal ─────────────────────────────────────────────────────

interface RevertChangesModalProps {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
}

export const RevertChangesModal = ({ open, onCancel, onConfirm }: RevertChangesModalProps) => {
  if (!open) {
    return null
  }

  return (
    <ModalPortal onBackdropClick={onCancel}>
      <div css={unsavedModalCardCss}>
        <div css={tw`px-6 pt-6`}>
          <div css={tw`mb-2 text-[17px] font-bold text-shades-900`}>Revert changes?</div>
          <div css={tw`mb-6 text-sm leading-relaxed text-shades-600`}>
            This will discard all unsaved changes and restore the last saved version of this flow.
          </div>
        </div>

        <div css={tw`flex justify-end gap-2 px-6 pb-6`}>
          <Button
            variant="outlined"
            size="small"
            color="white"
            onClick={onCancel}
            className={tw`!border-shades-200 !text-shades-700 hover:!bg-shades-50`}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            onClick={onConfirm}
            className={tw`!bg-critical-500 hover:!bg-critical-600 active:!bg-critical-600`}
          >
            Revert changes
          </Button>
        </div>
      </div>
    </ModalPortal>
  )
}

export const UnsavedChangesModal = ({ open, onLeave, onStay }: UnsavedChangesModalProps) => {
  if (!open) {
    return null
  }

  return (
    <ModalPortal onBackdropClick={onStay}>
      <div css={unsavedModalCardCss}>
        <div css={tw`px-6 pt-6`}>
          <div css={tw`mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-warning-100`}>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--mui-warning-600)"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <div css={tw`mb-2 text-[17px] font-bold text-shades-900`}>Unsaved changes</div>
          <div css={tw`mb-6 text-sm leading-relaxed text-shades-600`}>
            You have unsaved changes to this flow. If you leave now, your changes will be lost.
          </div>
        </div>

        <div css={tw`flex justify-end gap-2 px-6 pb-6`}>
          <Button
            variant="outlined"
            size="small"
            color="white"
            onClick={onStay}
            className={tw`!border-shades-200 !text-shades-700 hover:!bg-shades-50`}
          >
            Stay on page
          </Button>
          <Button
            size="small"
            color="primary"
            onClick={onLeave}
            className={tw`!bg-critical-500 hover:!bg-critical-600 active:!bg-critical-600`}
          >
            Leave anyway
          </Button>
        </div>
      </div>
    </ModalPortal>
  )
}
